import React, { useState } from 'react';
import { SidebarLayout, PageHeader } from '../components/layout';
import { OrganizationSettingsTab, DivisionSettingsTab } from '../components/configuration';
import { Tabs } from '../components/ui';
import { useAuth } from '../hooks/use-auth';
import { useDivisionContext } from '../contexts/division-context';
import { Division, MatchIdentifier } from '../types';

export function ConfigurationPage() {
  const { user, isAuthenticated, isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState('organization');
  const [showMatchIdentifierForm, setShowMatchIdentifierForm] = useState(false);
  const [showMatchIdentifierTest, setShowMatchIdentifierTest] = useState(false);
  const [editingMatchIdentifier, setEditingMatchIdentifier] = useState<MatchIdentifier | undefined>();
  const [testingMatchIdentifier, setTestingMatchIdentifier] = useState<MatchIdentifier | undefined>();

  // Use the global division context
  const { selectedDivisionId, setSelectedDivisionId } = useDivisionContext();

  const handleEditDivision = (division: Division) => {
    // Optional: Additional handling when a division is edited
    console.log('Editing division:', division);
  };

  const handleCreateMatchIdentifier = () => {
    setEditingMatchIdentifier(undefined);
    setShowMatchIdentifierForm(true);
  };

  const handleEditMatchIdentifier = (matchIdentifier: MatchIdentifier) => {
    setEditingMatchIdentifier(matchIdentifier);
    setShowMatchIdentifierForm(true);
  };

  const handleTestMatchIdentifier = (matchIdentifier: MatchIdentifier) => {
    setTestingMatchIdentifier(matchIdentifier);
    setShowMatchIdentifierTest(true);
  };

  const handleCloseMatchIdentifierForm = () => {
    setShowMatchIdentifierForm(false);
    setEditingMatchIdentifier(undefined);
  };

  const handleCloseMatchIdentifierTest = () => {
    setShowMatchIdentifierTest(false);
    setTestingMatchIdentifier(undefined);
  };

  if (!isAuthenticated) {
    return (
      <SidebarLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-secondary-900 dark:text-white mb-2">
              Authentication Required
            </h2>
            <p className="text-secondary-600 dark:text-secondary-400">
              Please log in to access the configuration section.
            </p>
          </div>
        </div>
      </SidebarLayout>
    );
  }

  if (!isAdmin) {
    return (
      <SidebarLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-secondary-900 dark:text-white mb-2">
              Access Denied
            </h2>
            <p className="text-secondary-600 dark:text-secondary-400">
              You don't have permission to access the configuration section.
              <br />
              Required roles: Admin or DivisionAdmin
            </p>
          </div>
        </div>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout>
      <div className="py-4">
        <PageHeader
          title="Configuration"
          subtitle="Manage organizational settings and divisions"
        />

        <Tabs
          activeTabId={activeTab}
          onTabChange={setActiveTab}
          tabs={[
            {
              id: 'organization',
              label: '🏢 Organization Settings',
              content: <OrganizationSettingsTab />,
            },
            {
              id: 'divisions',
              label: '📁 Division Management',
              content: (
                <DivisionSettingsTab
                  onEditDivision={handleEditDivision}
                  selectedDivisionId={selectedDivisionId}
                  setSelectedDivisionId={setSelectedDivisionId}
                  showMatchIdentifierForm={showMatchIdentifierForm}
                  setShowMatchIdentifierForm={setShowMatchIdentifierForm}
                  showMatchIdentifierTest={showMatchIdentifierTest}
                  setShowMatchIdentifierTest={setShowMatchIdentifierTest}
                  editingMatchIdentifier={editingMatchIdentifier}
                  testingMatchIdentifier={testingMatchIdentifier}
                  onCreateMatchIdentifier={handleCreateMatchIdentifier}
                  onEditMatchIdentifier={handleEditMatchIdentifier}
                  onTestMatchIdentifier={handleTestMatchIdentifier}
                  onCloseMatchIdentifierForm={handleCloseMatchIdentifierForm}
                  onCloseMatchIdentifierTest={handleCloseMatchIdentifierTest}
                />
              ),
            },
          ]}
        />
      </div>
    </SidebarLayout>
  );
}
